/**
 * useAudioLevel - Real-time microphone input level monitor
 *
 * Uses getUserMedia + AudioContext + AnalyserNode to sample the
 * microphone input level via requestAnimationFrame.  Returns a
 * normalised 0-1 value that can drive a visual indicator.
 *
 * We request noiseSuppression, echoCancellation, and autoGainControl so the
 * stream we measure is processed by the browser: background noise is reduced,
 * so the meter shows "good" only when the user is actually speaking, not
 * when ambient room noise is present. Aligns with ChartMind's mic constraints.
 *
 * SENSITIVITY_MULTIPLIER boosts the reading so normal/quiet speech
 * still moves the meter; raw RMS is often low from browser mic levels.
 *
 * The stream is acquired once when `enabled` becomes true and
 * released when `enabled` becomes false or the component unmounts.
 * Because SpeechRecognition already holds a mic permission grant
 * the getUserMedia call should succeed silently.
 */

import { useState, useRef, useEffect, useCallback } from 'react';

// Boost so quiet/normal speech produces a visible level (browser mics often read low)
const SENSITIVITY_MULTIPLIER = 3;

// Values at or below this (after boost) are treated as silence so the meter doesn't show "good" in a quiet room
const NOISE_FLOOR = 0.12;

const useAudioLevel = (enabled = false) => {
  const [level, setLevel] = useState(0);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafIdRef = useRef(null);
  const dataArrayRef = useRef(null);

  const poll = useCallback(() => {
    if (!analyserRef.current || !dataArrayRef.current) return;
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    // RMS (root-mean-square) of the frequency bins gives a smooth reading
    let sum = 0;
    const data = dataArrayRef.current;
    for (let i = 0; i < data.length; i++) {
      sum += data[i] * data[i];
    }
    const rms = Math.sqrt(sum / data.length) / 255; // normalise to 0-1
    const boosted = Math.min(1, rms * SENSITIVITY_MULTIPLIER);

    // Gate: treat near-silence as 0 so the indicator doesn't show "good/great" on room noise
    const gated = boosted <= NOISE_FLOOR ? 0 : (boosted - NOISE_FLOOR) / (1 - NOISE_FLOOR);
    setLevel(Math.min(1, gated));

    rafIdRef.current = requestAnimationFrame(poll);
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Tear down when disabled
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      if (sourceRef.current) { try { sourceRef.current.disconnect(); } catch (_) { /* */ } }
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (_) { /* */ } }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); }
      streamRef.current = null;
      audioCtxRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
      dataArrayRef.current = null;
      setLevel(0);
      return;
    }

    let cancelled = false;

    const start = async () => {
      try {
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });
        } catch (constraintErr) {
          console.warn('[useAudioLevel] Constraints not supported, using default mic:', constraintErr?.message);
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = ctx;

        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.6;
        analyserRef.current = analyser;

        const source = ctx.createMediaStreamSource(stream);
        sourceRef.current = source;

        // Boost gain so analyser sees a stronger signal (helps with quiet mics)
        const gainNode = ctx.createGain();
        gainNode.gain.value = 2.5;
        source.connect(gainNode);
        gainNode.connect(analyser);

        dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

        rafIdRef.current = requestAnimationFrame(poll);
      } catch (err) {
        console.warn('[useAudioLevel] Could not access microphone:', err);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      if (sourceRef.current) { try { sourceRef.current.disconnect(); } catch (_) { /* */ } }
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (_) { /* */ } }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); }
      streamRef.current = null;
      audioCtxRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
      dataArrayRef.current = null;
    };
  }, [enabled, poll]);

  return level;
};

export default useAudioLevel;
